// =============================================================================
// useStats Hook
// =============================================================================

import { useState, useCallback, useEffect, useRef } from 'react';
import { useLogCollectorClient } from './useLogCollectorClient.js';
import { ApiClientError, type SystemStats } from '../lib/api.js';

// =============================================================================
// Types
// =============================================================================

export interface UseStatsOptions {
  /** Base URL override */
  baseUrl?: string;
  /** Whether to fetch stats on mount (default: true) */
  fetchOnMount?: boolean;
  /** Auto-refresh interval in milliseconds (0 = disabled, default: 0) */
  refreshInterval?: number;
}

export interface UseStatsResult {
  /** System statistics */
  stats: SystemStats | null;
  /** Whether stats are being loaded */
  isLoading: boolean;
  /** Error from the last operation */
  error: string | null;
  /** Fetch/refresh stats */
  fetchStats: () => Promise<void>;
  /** Clear error state */
  clearError: () => void;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for fetching system statistics.
 *
 * @example
 * ```tsx
 * function Dashboard() {
 *   const { stats, isLoading, error, fetchStats } = useStats({
 *     refreshInterval: 30000, // Refresh every 30 seconds
 *   });
 *
 *   if (isLoading && !stats) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *   if (!stats) return <Empty />;
 *
 *   return (
 *     <div>
 *       <button onClick={fetchStats}>Refresh</button>
 *       <div>
 *         <h2>Overview</h2>
 *         <p>Apps: {stats.apps_count}</p>
 *         <p>API Keys: {stats.api_keys_count}</p>
 *         <p>Total Logs: {stats.total_logs}</p>
 *       </div>
 *       <div>
 *         <h2>Apps</h2>
 *         <ul>
 *           {stats.logs_by_app.map(app => (
 *             <li key={app.app_id}>
 *               {app.app_name}: {app.log_count} logs
 *               ({(app.db_size_bytes / 1024 / 1024).toFixed(2)} MB)
 *             </li>
 *           ))}
 *         </ul>
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 */
export function useStats(options: UseStatsOptions = {}): UseStatsResult {
  const { baseUrl, fetchOnMount = true, refreshInterval = 0 } = options;
  const { client } = useLogCollectorClient({ baseUrl });

  const [stats, setStats] = useState<SystemStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if component is mounted for async operations
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Fetch system stats.
   */
  const fetchStats = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await client.getStats();

      if (!mountedRef.current) return;

      setStats(result);
    } catch (err) {
      if (!mountedRef.current) return;

      if (err instanceof ApiClientError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to fetch stats');
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [client]);

  /**
   * Clear error state.
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Fetch on mount if enabled
  useEffect(() => {
    if (fetchOnMount) {
      fetchStats();
    }
  }, [fetchOnMount, fetchStats]);

  // Auto-refresh if interval is set
  useEffect(() => {
    if (refreshInterval <= 0) return;

    const intervalId = setInterval(() => {
      fetchStats();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [refreshInterval, fetchStats]);

  return {
    stats,
    isLoading,
    error,
    fetchStats,
    clearError,
  };
}
